/**
 * Reservation Page - Step 1: Vehicle Selection
 * Main reservation flow starting point
 */

'use client'

import { ReservationHeader } from '@/components/features/reservation/ReservationHeader'
import { ReservationStepper } from '@/components/features/reservation/ReservationStepper'
import { SearchModal } from '@/components/features/reservation/SearchModal'
import { VehicleCategories } from '@/components/features/reservation/VehicleCategories'
import { VehicleGrid } from '@/components/features/reservation/VehicleGrid'
import { useCars } from '@/hooks/useCars'
import { useStations } from '@/hooks/useStations'
import { formatDateForDisplay } from '@/lib/utils'
import { useReservationStore } from '@/store'
import type { CarCategory } from '@/types'
import { differenceInDays } from 'date-fns'
import { useRouter, useSearchParams } from 'next/navigation'
import { Suspense, useEffect, useMemo, useRef, useState } from 'react'

function HomePageContent() {
	const router = useRouter()
	const urlSearchParams = useSearchParams()
	const { searchParams, setSearchParams, setCar, setStep } =
		useReservationStore()
	const { stations } = useStations()
	const [selectedCategory, setSelectedCategory] = useState<string>('all')
	const [isSearchModalOpen, setIsSearchModalOpen] = useState(false)

	// Ref to track the last applied URL params key — prevents re-applying
	// when Next.js recreates the useSearchParams reference without URL change
	const appliedParamsKeyRef = useRef<string | null>(null)

	// Initialize from URL params (apply only when params actually change)
	useEffect(() => {
		const pickupStationId = urlSearchParams.get('pickupStationId')
		const dropoffStationId = urlSearchParams.get('dropoffStationId')
		const pickupDate = urlSearchParams.get('pickupDate')
		const dropoffDate = urlSearchParams.get('dropoffDate')
		const driverAge = urlSearchParams.get('driverAge')
		const countryCode = urlSearchParams.get('countryCode')
		const promotionalCode =
			urlSearchParams.get('promotionalCode') ||
			urlSearchParams.get('PromotionalCode')

		if (pickupStationId && pickupDate && dropoffDate) {
			// Build a stable key from the actual param values
			const paramsKey = `${pickupStationId}|${dropoffStationId ?? pickupStationId}|${pickupDate}|${dropoffDate}|${driverAge}|${countryCode}`

			// Skip if same params already applied — prevents overriding user changes
			// when useSearchParams() returns a new object reference on re-renders
			if (appliedParamsKeyRef.current === paramsKey) return

			appliedParamsKeyRef.current = paramsKey
			setSearchParams({
				pickupStationId,
				dropoffStationId: dropoffStationId || pickupStationId,
				pickupDate,
				dropoffDate,
				driverAge: driverAge ? parseInt(driverAge) : 25,
				countryCode: countryCode || 'CO',
				promotionalCode: promotionalCode || undefined,
			})
		}
	}, [urlSearchParams, setSearchParams])

	const { cars, isLoading } = useCars({
		searchParams,
		filters: {
			category:
				selectedCategory !== 'all'
					? [selectedCategory as CarCategory]
					: undefined,
		},
	})

	// Derived state for header
	const headerInfo = useMemo(() => {
		const pickupStation = stations.find(
			s => String(s.id) === String(searchParams.pickupStationId),
		)
		const returnStation = stations.find(
			s => String(s.id) === String(searchParams.dropoffStationId),
		)
		const days = differenceInDays(
			new Date(searchParams.dropoffDate),
			new Date(searchParams.pickupDate),
		)

		return {
			pickupLocation: pickupStation?.name || searchParams.pickupStationId,
			dropoffLocation: returnStation?.name || searchParams.dropoffStationId,
			pickupDate: formatDateForDisplay(searchParams.pickupDate),
			dropoffDate: formatDateForDisplay(searchParams.dropoffDate),
			days: Math.max(1, days),
		}
	}, [searchParams, stations])

	const handleSelectVehicle = (vehicleId: string) => {
		const vehicle = cars.find(c => c.id === vehicleId)
		if (vehicle) {
			setCar(vehicle)
			setStep(2)
			router.push('/reserva/step-2')
		}
	}

	const handleModifyReservation = () => {
		setIsSearchModalOpen(true)
	}

	const handleCategorySelect = (categoryId: string) => {
		setSelectedCategory(categoryId)
	}

	return (
		<div className='min-h-screen bg-gray-50'>
			{/* Header con información de reserva */}
			<ReservationHeader
				pickupLocation={headerInfo.pickupLocation}
				dropoffLocation={headerInfo.dropoffLocation}
				pickupDate={headerInfo.pickupDate}
				dropoffDate={headerInfo.dropoffDate}
				days={headerInfo.days}
				onModify={handleModifyReservation}
			/>

			{/* Stepper */}
			<div className='py-8'>
				<ReservationStepper currentStep={1} />
			</div>

			{/* Categorías de vehículos */}
			<VehicleCategories
				selectedCategory={selectedCategory}
				onCategoryChange={handleCategorySelect}
			/>

			{/* Grid de vehículos */}
			{isLoading ? (
				<div className='flex justify-center py-12'>
					<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#09194D]'></div>
				</div>
			) : (
				<VehicleGrid
					vehicles={cars}
					onReserve={handleSelectVehicle}
				/>
			)}

			<SearchModal
				isOpen={isSearchModalOpen}
				onClose={() => setIsSearchModalOpen(false)}
			/>
		</div>
	)
}

export default function HomePage() {
	return (
		<Suspense
			fallback={
				<div className='flex justify-center py-12'>
					<div className='animate-spin rounded-full h-12 w-12 border-b-2 border-[#09194D]'></div>
				</div>
			}
		>
			<HomePageContent />
		</Suspense>
	)
}
